import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { money, fdate } from "../api";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";
import Bubbles from "../components/Bubbles";
import InfoTip from "../components/InfoTip";
import { VEHICLE_LABELS } from "../components/bits";
import { ArrowRight } from "lucide-react";

export function PublicJobCard({ job }) {
  return (
    <Link to={`/loads/${job.ref}`} data-testid={`public-job-${job.ref}`}
      className="cr-card-glass !p-5 block">
      <div className="flex items-center justify-between mb-1.5">
        <span className="font-mono text-[11px] text-[#A09D95]">{job.ref}</span>
        {job.price_net != null && job.pricing_mode !== "bidding" && (
          <span className="cr-pill-yellow">{money(job.price_gross ?? job.price_net, job.currency)}</span>
        )}
      </div>
      <p className="text-sm font-semibold text-[#1C1B1A]">
        {job.pickup?.city || "—"}{job.pickup?.country ? `, ${job.pickup.country}` : ""}
        <span className="text-[#A09D95] mx-1.5">→</span>
        {job.delivery?.city || "—"}{job.delivery?.country ? `, ${job.delivery.country}` : ""}
      </p>
      <p className="text-xs text-[#78756E] mt-1">
        {VEHICLE_LABELS[job.vehicle_type] || job.vehicle_type}
        {job.cargo?.weight_kg ? ` · ${job.cargo.weight_kg} kg` : ""}
        {job.pickup_date ? ` · ${fdate(job.pickup_date)}` : ""}
        {job.distance_km ? ` · ${job.distance_km} km` : ""}
      </p>
    </Link>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [coverage, setCoverage] = useState([]);
  useEffect(() => {
    api.get("/public/home").then((r) => setData(r.data)).catch(() => {});
    api.get("/public/coverage").then((r) => setCoverage(r.data.countries || [])).catch(() => {});
  }, []);
  const home = data?.home || {};
  const stats = data?.stats || {};

  return (
    <div>
      <Seo title={data?.seo?.site_title || "CourierRadar — Courier jobs and delivery capacity across Europe"}
        description={data?.seo?.site_description} canonical="https://courierradar.com/"
        jsonLd={{ "@context": "https://schema.org", "@type": "WebSite", name: "CourierRadar", url: "https://courierradar.com" }} />

      {data?.banners?.map((b) => (
        <div key={b.id} data-testid={`banner-${b.id}`} className="bg-black text-white text-center text-xs py-2 px-4">
          {b.title} {b.cta_url && <a href={b.cta_url} className="underline underline-offset-2 ml-1">{b.cta_label || "Learn more"}</a>}
        </div>
      ))}

      <section className="relative">
        <Bubbles />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 min-h-[calc(88svh-4.5rem)] flex flex-col items-center justify-center text-center py-12">
          <h1 className="font-heading font-semibold tracking-tight text-[#1C1B1A] leading-[1.05]" style={{ fontSize: "clamp(2.2rem, 6.5vw, 4.2rem)" }}>
            {home.hero_title || "Transport work, settled."}
          </h1>
          <p className="text-[#78756E] mt-4 max-w-md mx-auto leading-relaxed" style={{ fontSize: "clamp(0.85rem, 1.6vw, 1rem)" }}>
            {home.hero_subtitle || "Verified companies post real delivery work. Approved couriers bid, run and get paid — with live tracking and proof of delivery built in."}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-7 w-full sm:w-auto">
            {user ? (
              <Link to={user.role === "admin" ? "/control" : user.role === "company" ? "/company" : "/courier"}
                data-testid="hero-dashboard-cta" className="cr-btn-dark !px-8 !py-3.5">
                Open your dashboard
              </Link>
            ) : (
              <>
                <Link to="/auth?mode=register&role=courier" data-testid="hero-courier-cta" className="cr-btn-dark !px-8 !py-3.5">
                  Join as a courier
                </Link>
                <Link to="/auth?mode=register&role=company" data-testid="hero-company-cta" className="cr-btn-yellow !px-8 !py-3.5">
                  Post a job
                </Link>
              </>
            )}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-14">
        <div className="grid sm:grid-cols-3 gap-3" data-testid="home-services">
          {[
            ["Same-day & hotshot", "Urgent courier work collected within hours and driven direct — fixed price or open bidding.", "/services"],
            ["Dedicated routes", "The same approved courier on your recurring runs, on your schedule, with consolidated invoicing.", "/services"],
            ["Multi-drop & part loads", "Group several drops into one optimised run and pay for the space you use.", "/services"],
          ].map(([t, d, to]) => (
            <Link key={t} to={to} data-testid={`home-service-${t.split(" ")[0].toLowerCase()}`} className="cr-card-glass block">
              <p className="text-sm font-bold mb-1.5">{t}</p>
              <p className="text-xs text-[#78756E] leading-relaxed">{d}</p>
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-14" data-testid="home-two-sides">
        <h2 className="font-heading text-xl sm:text-2xl font-semibold tracking-tight mb-2">One marketplace, both sides of the road</h2>
        <p className="text-xs text-[#78756E] mb-6 max-w-xl leading-relaxed">CourierRadar exists to remove the friction between companies with freight and the carriers who move it — real work, verified people, live tracking and tidy paperwork on every single job.</p>
        <div className="grid lg:grid-cols-2 gap-3">
          <div className="cr-card !p-7" data-testid="home-side-shippers">
            <p className="cr-label !text-[#A09D95] mb-2">For shippers</p>
            <h3 className="font-heading text-lg font-semibold tracking-tight mb-2">Post a load in a minute, watch it move</h3>
            <ul className="space-y-2 text-xs text-[#78756E] leading-relaxed">
              <li>· Approved, document-checked carriers bid on your load — you compare price, rating and timing in one view</li>
              <li>· Live GPS tracking and signed proof of delivery on every job, shareable with your customers</li>
              <li>· One invoice trail from booking to settled payment — no email chains, no spreadsheet reconciling</li>
              <li>· API, CSV imports and webhooks when you're ready to plug it into your own systems</li>
            </ul>
            <div className="flex flex-wrap gap-2 mt-5">
              <Link to="/shippers" data-testid="home-shippers-cta" className="cr-btn-dark">Why shippers join</Link>
              <Link to="/auth?mode=register&role=company" data-testid="home-shipper-join" className="cr-btn-yellow">Post your first load</Link>
            </div>
          </div>
          <div className="cr-card !p-7" data-testid="home-side-carriers">
            <p className="cr-label !text-[#A09D95] mb-2">For carriers</p>
            <h3 className="font-heading text-lg font-semibold tracking-tight mb-2">Your van, your price, steady real work</h3>
            <ul className="space-y-2 text-xs text-[#78756E] leading-relaxed">
              <li>· A map-first load board of genuine jobs from verified companies across the UK and Europe</li>
              <li>· Bid your own price or grab fixed-price loads instantly — no dispatcher shaving your margin</li>
              <li>· Invoices trigger automatically on delivery; you confirm when the money lands</li>
              <li>· Two-way reviews build a public reputation that wins you better loads over time</li>
            </ul>
            <div className="flex flex-wrap gap-2 mt-5">
              <Link to="/carriers" data-testid="home-carriers-cta" className="cr-btn-dark">Why carriers join</Link>
              <Link to="/auth?mode=register&role=courier" data-testid="home-carrier-join" className="cr-btn-yellow">Start carrying</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-14" data-testid="home-ai-band">
        <div className="bg-[#1C1B1A] text-white rounded-[28px] p-8 sm:p-10 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#F2D06B] opacity-30 blur-3xl pointer-events-none" />
          <div className="relative">
            <p className="cr-label !text-[#F2D06B] mb-2">Radar AI · built into CourierRadar</p>
            <h2 className="font-heading text-xl sm:text-2xl font-semibold tracking-tight mb-2">Ask anything. It's handled.</h2>
            <p className="text-xs text-white/70 leading-relaxed max-w-lg mb-5">Tell Radar what you need — it searches, bids, books, publishes, assigns, schedules and messages for you, on your real jobs, bids, documents and invoices. Payments, publishing and assignments always ask first — everything is audit-logged.</p>
            <div className="flex flex-wrap gap-2 mb-6">
              {["Find van jobs in Germany — and bid on the best one", "Book a van, Berlin → Paris, Friday 8am", "Compare couriers on CR-… and assign the winner", "Email me a report every morning"].map((q) => (
                <span key={q} className="text-[11px] bg-white/10 border border-white/15 rounded-full px-3 py-1.5 text-white/85">“{q}”</span>
              ))}
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mb-7 text-[11px] text-white/70">
              {["Real platform data only — never invented", "You set the permission mode", "Critical actions always confirmed"].map((t) => (
                <p key={t} className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-[#A855F7] to-[#F2D06B] shrink-0" />{t}</p>
              ))}
            </div>
            <Link to="/ai" data-testid="home-ai-cta" className="cr-btn-yellow whitespace-nowrap">Meet Radar</Link>
          </div>
        </div>
      </section>

      {(stats.couriers > 0 || stats.companies > 0 || stats.jobs_delivered > 0 || stats.open_jobs > 0) && (
        <section className="border-t border-[#E8E4D9]/70" data-testid="live-stats">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 flex flex-wrap justify-center gap-x-10 gap-y-3 text-center">
            {[
              ["Couriers", stats.couriers], ["Companies", stats.companies],
              ["Jobs delivered", stats.jobs_delivered], ["Open now", stats.open_jobs],
            ].filter(([, v]) => v > 0).map(([label, v]) => (
              <div key={label}><p className="font-heading text-xl font-bold tracking-tight">{v}</p>
                <p className="cr-label mt-0.5">{label}</p></div>
            ))}
          </div>
        </section>
      )}

      <section className="border-t border-[#E8E4D9]/70">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
          <h2 className="font-heading text-xl sm:text-2xl font-semibold tracking-tight mb-8">How it works</h2>
          <div className="grid sm:grid-cols-3 gap-3">
            {[["Post or find work", "Companies post jobs with route, vehicle and dates. Couriers see matching jobs instantly."],
              ["Bid & assign", "Couriers bid or instantly accept. Companies compare and assign in one click."],
              ["Track & prove", "Live GPS tracking, status updates, signed POD and invoices — all in one place."]].map(([t, b], i) => (
              <div key={t} className="cr-card-glass">
                <p className="font-mono text-[11px] text-[#A09D95] mb-2">0{i + 1}</p>
                <p className="text-sm font-bold">{t}</p>
                <p className="text-xs text-[#78756E] mt-1.5 leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-[#E8E4D9]/70">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-heading text-xl sm:text-2xl font-semibold tracking-tight">Platform API</h2>
            <p className="text-sm text-[#78756E] mt-2 max-w-md">Create jobs, receive status webhooks and pull PODs from your own systems. Available on Company Pro and Enterprise.</p>
          </div>
          <pre className="bg-[#1C1B1A] text-[#F6F3EA] rounded-[28px] p-5 text-[11px] leading-relaxed overflow-x-auto font-mono" data-testid="api-example">{`POST /api/v1/jobs
X-API-Key: crk_…

{ "pickup": {…}, "delivery": {…},
  "vehicle_type": "van_small" }`}</pre>
        </div>
      </section>

      <section className="border-t border-[#E8E4D9]/70">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
          <h2 className="font-heading text-xl sm:text-2xl font-semibold tracking-tight mb-6">Coverage</h2>
          <div className="flex flex-wrap gap-2">
            {coverage.slice(0, 14).map((c) => (
              <Link key={c.code} to={`/courier-jobs/${c.code}`} data-testid={`home-country-${c.code}`}
                className="cr-pill-outline hover:border-[#1C1B1A]/30 transition-colors !px-3.5 !py-1.5">
                {c.name}{c.jobs > 0 && <span className="ml-1.5 font-bold">{c.jobs}</span>}
              </Link>
            ))}
            <Link to="/courier-jobs" className="text-xs font-semibold underline underline-offset-2 px-2 py-1.5">All countries →</Link>
          </div>
        </div>
      </section>

      <section className="border-t border-[#E8E4D9]/70" data-testid="home-resources">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <p className="cr-label !text-[#A09D95] mb-3">Transport resources — live official data</p>
          <div className="flex flex-wrap gap-2">
            {[["/resources/news", "Transport News"], ["/resources/road-alerts", "Road Alerts"],
              ["/resources/fuel-prices", "Fuel Prices"], ["/resources/driver-rules", "Driver Rules"]].map(([to, label]) => (
              <Link key={to} to={to} data-testid={`home-resource-${label.split(" ")[1]?.toLowerCase() || label.toLowerCase()}`}
                className="cr-pill-outline hover:border-[#1C1B1A]/30 transition-colors !px-3.5 !py-1.5">{label}</Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
